import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Upload, Search } from 'lucide-react';
import ImportDialog from '@/components/ImportDialog';
import { trNorm } from '@/lib/arama';

const recipeImportColumns = [
  { key: 'name', label: 'Recete' },
  { key: 'product_code', label: 'Urun Kodu' },
  { key: 'yield_quantity', label: 'Uretim Miktari' },
  { key: 'yield_unit', label: 'Uretim Birimi' },
  { key: 'ingredients', label: 'Malzeme', render: (r) => {
    const errCount = (r.ingredients || []).filter(i => i.unit_error).length;
    return errCount > 0
      ? `${r.ingredients?.length || 0} kalem (${errCount} hatali)`
      : `${r.ingredients?.length || 0} kalem`;
  }},
];

export default function RecipeList() {
  const [recipes, setRecipes] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [search, setSearch] = useState('');

  const fetchRecipes = () => api.get('/recipes').then(r => setRecipes(r.data)).catch(() => {});
  useEffect(() => { fetchRecipes(); }, []);

  const filteredRecipes = useMemo(() => {
    if (!search.trim()) return recipes;
    const q = trNorm(search);
    return recipes.filter(r =>
      trNorm(r.product_name).includes(q) ||
      trNorm(r.product_code).includes(q)
    );
  }, [recipes, search]);

  return (
    <div data-testid="recipe-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Receteler</h1>
          <p className="text-sm text-[#8a7e70] mt-1">{filteredRecipes.length} / {recipes.length} recete</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setShowImport(true)} variant="outline"
            className="border-[#d0c4b0] text-[#3F3E39] hover:bg-[#e8dfd0] font-semibold" data-testid="import-recipes-btn">
            <Upload size={16} className="mr-2" /> Ice Aktar
          </Button>
          <Link to="/erp/recipes/new">
            <Button className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="new-recipe-btn">
              <Plus size={16} className="mr-2" /> Yeni Recete
            </Button>
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
          <Input 
            placeholder="Recete veya kod ara..." 
            value={search} 
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-10 bg-white border-[#d0c4b0]" 
            data-testid="recipe-search"
          />
        </div>
      </div>

      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Urun</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Kod</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-center">Versiyon</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Uretim</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Malzeme</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Maliyet</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRecipes.map((r) => (
              <TableRow key={r.id} className="erp-table-row border-[#d0c4b0]">
                <TableCell>
                  <Link to={`/erp/recipes/${r.id}`} className="font-medium text-[#1a1916] hover:underline" data-testid={`recipe-link-${r.id}`}>
                    {r.product_name}
                  </Link>
                </TableCell>
                <TableCell className="font-mono text-sm text-[#8a7e70]">{r.product_code}</TableCell>
                <TableCell className="text-center">
                  <span className="status-badge bg-[#e8dfd0] text-[#3F3E39]">v{r.version}</span>
                </TableCell>
                <TableCell className="text-right text-[#3F3E39]">{r.yield_quantity} {r.yield_unit}</TableCell>
                <TableCell className="text-right text-[#8a7e70]">{r.ingredients?.length || 0} kalem</TableCell>
                <TableCell className="text-right">
                  <span className={`font-semibold ${r.total_cost > 0 ? 'text-[#3d6e4e]' : 'text-[#8a7e70]'}`}>
                    {r.total_cost > 0 ? `₺${r.total_cost.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : '-'}
                  </span>
                </TableCell>
              </TableRow>
            ))}
            {filteredRecipes.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#8a7e70]">Recete bulunamadi</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <ImportDialog open={showImport} onOpenChange={setShowImport} config={{
        title: 'Receteler Ice Aktar',
        templateUrl: '/api/erp/import/template/recipes',
        previewUrl: '/import/recipes/preview',
        executeUrl: '/import/recipes/execute',
        columns: recipeImportColumns,
        onComplete: fetchRecipes
      }} />
    </div>
  );
}
